import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AppLoader } from '@hero/atoms';
import { configs } from '@hero/configs';
import {
  AUTOMATION_CREATION_STEPS,
  CARDISLE_GREETING_CARD,
  GIFT_MESSAGE_TYPE,
  OCCASION_TYPES
} from '@hero/constants';
import { useAutomationCreationHeader } from '@hero/hooks';
import { PageLayout } from '@hero/layouts';
import { CustomizeMessageModal } from '@hero/molecules';
import { CreationStepExperience } from '@hero/organisms';
import { ROUTE_PATHS } from '@hero/routes';
import { useUpdateAutomation } from '@hero/services';
import { useGetBranding } from '@hero/services/organization-service/hooks';
import { useNewAutomationStore, useValidateRules } from '@hero/store/hooks';
import { cleanObjectEmptyFields, getAddonData } from '@hero/utils';
import {
  getBrandingRule,
  getMessageAndCardParamsRule,
  getMessageParamsRule,
  getSubjectLineRule
} from '@hero/validations';

import * as S from './style';

function NewAutomationExperience() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isPageLoading, setIsPageLoading] = useState(false);

  const header = useAutomationCreationHeader({
    step: 4,
    setIsPageLoading
  });

  const { mutateAsync: updateAutomation, isLoading: isLoadingUpdateAutomation } =
    useUpdateAutomation();

  const {
    name,
    type,
    code,
    contacts,
    settings,
    experienceSettings,
    addons,
    onUpdateBranding,
    onUpdateOccasion,
    onUpdateMessageParams,
    onUpdateSharingMethod,
    updateAddons,
    updateExperienceSettings,
    reset
  } = useNewAutomationStore();

  const { data: { branding: defaultBranding } = {} } = useGetBranding();

  const { sharingMethod, messageParams, branding } = experienceSettings;

  const { validate } = useValidateRules();

  const digitalGreetingCard = getAddonData(addons, CARDISLE_GREETING_CARD);

  const giftMessageType = useMemo(() => {
    if (digitalGreetingCard) return GIFT_MESSAGE_TYPE.cardMessage;
    if (messageParams?.message) return GIFT_MESSAGE_TYPE.inputMessage;
    return null;
  }, [digitalGreetingCard, messageParams]);
  const [activeGiftMessage, setActiveGiftMessage] = useState(giftMessageType);

  const onNextButtonClick = async () => {
    const giftMessageRules = configs?.experience?.greetingCardEnabled
      ? {
          rule: getMessageAndCardParamsRule(activeGiftMessage),
          value: { ...messageParams, digitalGreetingCard: !!digitalGreetingCard }
        }
      : { rule: getMessageParamsRule(), value: messageParams };

    const isValid = await validate(
      [
        { rule: getBrandingRule(), value: branding },
        { ...giftMessageRules },
        { rule: getSubjectLineRule(), value: sharingMethod?.params?.subject }
      ].filter(item => item.rule)
    );
    if (!isValid) return;

    setIsPageLoading(true);
    try {
      await updateAutomation({
        params: {
          code
        },
        body: {
          experienceSettings: {
            messageParams,
            branding,
            sharingMethod
          },
          meta: {
            creationStep: AUTOMATION_CREATION_STEPS.newAutomationReviewActive
          },
          settings: cleanObjectEmptyFields(settings),
          addons
        }
      });
      navigate(ROUTE_PATHS.NEW_AUTOMATION_REVIEW_ACTIVE(code), { replace: true });

      setTimeout(() => {
        reset();
        setIsPageLoading(false);
      }, 100);
    } finally {
      setIsPageLoading(false);
    }
  };

  useEffect(() => {
    if (!name) {
      navigate(ROUTE_PATHS.MY_AUTOMATION_LIST);
    }
  }, []);

  useEffect(() => {
    onUpdateBranding({
      logoUrl: branding?.logoUrl || defaultBranding?.logoUrl,
      giftWrapperUrl: branding?.giftWrapperUrl || defaultBranding?.giftWrappers?.default
    });
  }, [defaultBranding]);

  const onClickBackButton = () => navigate(ROUTE_PATHS.NEW_AUTOMATION_RECIPIENTS);

  return (
    <PageLayout
      header={header}
      navigation={{
        rightButton: {
          children: t('next'),
          onClick: onNextButtonClick,
          icon: true
        },
        leftButton: {
          children: t('back'),
          loading: isLoadingUpdateAutomation,
          onClick: onClickBackButton
        }
      }}
      isLoading={isLoadingUpdateAutomation}
    >
      <S.HeadTitle variant="subtitle4Bold">{t('experienceHeaderSubTitle')}</S.HeadTitle>
      <CreationStepExperience
        messageParams={messageParams}
        branding={branding}
        sharingMethod={sharingMethod}
        onUpdateBranding={onUpdateBranding}
        onUpdateOccasion={onUpdateOccasion}
        onUpdateMessageParams={onUpdateMessageParams}
        onUpdateSharingMethod={onUpdateSharingMethod}
        onUpdateAddons={updateAddons}
        featureType="automation"
        giftMessageType={giftMessageType}
        activeGiftMessage={activeGiftMessage}
        setActiveGiftMessage={setActiveGiftMessage}
        shortcutTagsActive={false}
        defaultOccasion={OCCASION_TYPES[type]}
        selectedContacts={contacts.ids}
        subjectEnabled
        collectionDescriptionEnabled={false}
        addons={addons}
        renderCustomizeMessageModal={({
          isOpenCustomizeMessageModal,
          setOpenCustomizeMessageModal
        }) => {
          return (
            <CustomizeMessageModal
              editable
              isOpen={isOpenCustomizeMessageModal}
              setIsOpen={setOpenCustomizeMessageModal}
              selectedContacts={contacts?.ids}
              defaultMessage={messageParams?.message}
              customizedRecipientMessages={messageParams?.customizedRecipientMessages}
              updateCustomizedRecipientMessages={customizeMessages => {
                const newExperienceSettings = {
                  ...experienceSettings,
                  messageParams: {
                    ...experienceSettings?.messageParams,
                    customizedRecipientMessages: customizeMessages
                  }
                };
                updateExperienceSettings(newExperienceSettings);
              }}
            />
          );
        }}
      />
      {isPageLoading && <AppLoader />}
    </PageLayout>
  );
}

export { NewAutomationExperience };
